import {Component, OnInit} from '@angular/core';
import {TranslocoPipe} from "@jsverse/transloco";
import {ActivatedRoute, RouterLink} from '@angular/router';
import {Service} from '../../../core/entities/service/service.entity';
import {ServiceService} from '../../services/service/service.service';
import {AppImageComponent} from '../../../core/components/image/image';
import {CurrencyPipe} from '@angular/common';
import {BookingService} from '../../services/booking/booking.service';
import {TrackingService} from '../../../core/services/tracking/tracking.service';
import {TrackingEventName} from '../../../core/entities/tracking/tracking-event.entity';

@Component({
  selector: 'app-booking-create-cancel',
  imports: [
    TranslocoPipe,
    RouterLink,
    AppImageComponent,
    CurrencyPipe
  ],
  templateUrl: './booking-create-cancel.component.html',
  styleUrl: './booking-create-cancel.component.scss'
})
export class BookingCreateCancelComponent implements OnInit {
  public photographerSlug: string|null = null;
  public serviceId: number|null = null;
  public bookingId: number|null = null;
  public service: Service|null = null;
  public billingInfo: BillingInfo|null = null;
  public customerEmail: string|null = null;
  private paymentTracked = false;
  private serviceResolved = false;
  private billingInfoResolved = false;

  public constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly serviceService: ServiceService,
    private readonly bookingService: BookingService,
    private readonly tracking: TrackingService,
  ) {
  }

  public ngOnInit(): void {
    const slug = this.activatedRoute.snapshot.paramMap.get('slug');
    const service = this.activatedRoute.snapshot.paramMap.get('service');
    const bookingIdParam = this.activatedRoute.snapshot.paramMap.get('id');

    this.photographerSlug = slug;
    const parsedService = service ? parseInt(service, 10) : NaN;
    this.serviceId = Number.isFinite(parsedService) ? parsedService : null;
    this.bookingId = bookingIdParam ? parseInt(bookingIdParam, 10) : null;

    this.customerEmail = this.activatedRoute.snapshot.queryParamMap.get('email');
    const qpService = this.activatedRoute.snapshot.queryParamMap.get('service');
    if (!this.serviceId && qpService) {
      this.serviceId = parseInt(qpService, 10) || null;
    }

    if (this.serviceId) {
      this.loadService(this.serviceId);
    } else {
      this.serviceResolved = true;
    }
    this.tryLoadBillingInfos();
  }

  private loadService(serviceId: number): void {
    this.serviceService.getService(serviceId).subscribe({
      next: (srv) => {
        this.service = srv;
        this.serviceResolved = true;
        this.trackPaymentFailed();
      },
      error: () => {
        this.service = null;
        this.serviceResolved = true;
        this.trackPaymentFailed();
      }
    });
  }

  private tryLoadBillingInfos(): void {
    if (!this.bookingId || !this.customerEmail) {
      this.billingInfoResolved = true;
      this.trackPaymentFailed();
      return;
    }
    this.bookingService.getBillingInfos(this.bookingId, this.customerEmail).subscribe({
      next: (info) => {
        this.billingInfo = info;
        this.billingInfoResolved = true;
        this.trackPaymentFailed();
      },
      error: () => {
        this.billingInfo = null;
        this.billingInfoResolved = true;
        this.trackPaymentFailed();
      }
    });
  }

  private trackPaymentFailed(): void {
    if (this.paymentTracked || !this.bookingId || !this.billingInfoResolved || !this.serviceResolved) return;

    const serviceId = this.service?.id ?? this.serviceId;
    if (!serviceId) {
      return;
    }

    const basePrice = this.billingInfo
      ? (this.billingInfo.publicPrice ?? null)
      : this.service?.publicPrice ?? null;
    const discount = this.billingInfo?.discount ?? 0;
    const amount = basePrice != null ? basePrice - discount : null;

    const flow = this.tracking.getLastFlow() ?? 'service_page';
    this.tracking.track(TrackingEventName.CHECKOUT_CANCELLED, {
      booking_id: this.bookingId,
      service_id: serviceId,
      service_name: this.service?.name,
      amount,
      currency: 'EUR',
      discount,
      deposit_amount: this.billingInfo?.depositAmount ?? null,
      status: this.billingInfo?.status ?? 'failed',
    }, {
      gaEventName: 'checkout_cancelled',
      idempotencyKey: this.bookingId ? `checkout:${this.bookingId}:CANCELLED` : undefined,
      flow,
    });

    this.paymentTracked = true;
  }
}

interface BillingInfo {
  paymentDate: string|null;
  status: string;
  datetime: string;
  publicPrice: number;
  discount: number|null;
  depositAmount: number|null;
  invoiceUrl: string|null;
}
